import { fetchConfig } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { ThemeEvents } from '@theme/events';
import { propertiesFor } from '@theme/cart-attribution';

/**
 * @typedef {Object} Milestone
 * @property {number} threshold - The cart total threshold in cents.
 * @property {number|null} variantId - The gift variant id, or null if no gift.
 * @property {string} message - The gift message.
 * @property {boolean} subscriptionSatisfies - Whether a subscription in the cart meets this
 *   milestone's threshold outright, regardless of the cart total.
 */

/**
 * @typedef {Object} MilestoneConfig
 * @property {Milestone[]} milestones - The configured milestones.
 * @property {string[]} currentGiftKeys - The line item keys of current gifts.
 */

/**
 * Cart Gift Manager
 *
 * Manages free gift line items based on cart milestone thresholds.
 * When a milestone with a gift product is achieved, the gift is auto-added.
 * When the milestone is no longer met, the gift is auto-removed.
 */
class CartGiftManager {
  /**
   * Guards against overlapping evaluations.
   * @type {boolean}
   */
  #isProcessing = false;

  /**
   * Wires up event listeners and runs an initial evaluation.
   */
  init() {
    // Listen for cart updates and re-evaluate gifts
    document.addEventListener(ThemeEvents.cartUpdate, () => {
      // Small delay to allow the cart-items-component to finish its own update first
      setTimeout(() => this.evaluateGifts(), 500);
    });

    // Evaluate on initial page load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.evaluateGifts());
    } else {
      this.evaluateGifts();
    }
  }

  /**
   * Reads milestone gift configuration from the DOM.
   * @returns {MilestoneConfig | null}
   */
  #getMilestoneConfig() {
    const el = document.querySelector('script[data-cart-milestone-gifts]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent ?? '');
    } catch {
      return null;
    }
  }

  /**
   * Fetches the current cart state from /cart.js.
   * @returns {Promise<any|null>}
   */
  async #fetchCart() {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return null;
      return await response.json();
    } catch {
      return null;
    }
  }

  /**
   * Calculates the adjusted cart total by excluding free gift items.
   * @param {any} cart - The cart object from /cart.js
   * @returns {number} Adjusted total in cents
   */
  #getAdjustedTotal(cart) {
    let giftTotal = 0;
    for (const item of cart.items) {
      if (item.properties && item.properties._free_gift === 'true') {
        giftTotal += item.final_line_price;
      }
    }
    return cart.total_price - giftTotal;
  }

  /**
   * Reports whether the cart holds any subscription (selling plan) line item.
   * @param {any} cart - The cart object from /cart.js
   * @returns {boolean}
   */
  #hasSubscription(cart) {
    return cart.items.some((item) => item.selling_plan_allocation != null);
  }

  /**
   * Finds existing free gift items in the cart, keyed by variant ID.
   * @param {any} cart - The cart object
   * @returns {Map<number, any>} Map of variant ID → cart item
   */
  #getExistingGifts(cart) {
    const gifts = new Map();
    for (const item of cart.items) {
      if (item.properties && item.properties._free_gift === 'true') {
        gifts.set(item.variant_id, item);
      }
    }
    return gifts;
  }

  /**
   * Adds a gift product to the cart.
   * @param {number} variantId
   * @param {string} message
   * @returns {Promise<boolean>}
   */
  async #addGift(variantId, message) {
    const giftProperties = propertiesFor('free_gift');
    try {
      const body = JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: {
              _free_gift: 'true',
              _free_gift_message: message,
              ...giftProperties,
            },
          },
        ],
      });
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', { body }));
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Removes a gift product from the cart by setting its quantity to 0.
   * @param {string} lineItemKey - The cart item key
   * @returns {Promise<boolean>}
   */
  async #removeGift(lineItemKey) {
    try {
      const body = JSON.stringify({
        id: lineItemKey,
        quantity: 0,
      });
      const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));
      return response.ok;
    } catch {
      return false;
    }
  }

  /**
   * Re-renders the cart drawer sections after gift changes.
   */
  #refreshCartUI() {
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent instanceof HTMLElement && cartItemsComponent.dataset.sectionId) {
      sectionRenderer.renderSection(cartItemsComponent.dataset.sectionId, { cache: false });
    }
  }

  /**
   * Main evaluation: compare cart state against milestones, add/remove gifts.
   * @returns {Promise<void>}
   */
  async evaluateGifts() {
    if (this.#isProcessing) return;
    this.#isProcessing = true;

    try {
      const config = this.#getMilestoneConfig();
      if (!config) return;

      const cart = await this.#fetchCart();
      if (!cart) return;

      const adjustedTotal = this.#getAdjustedTotal(cart);
      const hasSubscription = this.#hasSubscription(cart);
      const existingGifts = this.#getExistingGifts(cart);

      // Determine which gift variant IDs should be in the cart
      const desiredGiftVariantIds = new Set();
      const desiredGiftMessages = new Map();

      for (const milestone of config.milestones) {
        if (milestone.variantId == null) continue;
        const earned =
          adjustedTotal >= milestone.threshold || (hasSubscription && milestone.subscriptionSatisfies);
        if (earned) {
          desiredGiftVariantIds.add(milestone.variantId);
          desiredGiftMessages.set(milestone.variantId, milestone.message);
        }
      }

      let cartChanged = false;

      // Remove gifts that are no longer qualified
      for (const [variantId, item] of existingGifts) {
        if (!desiredGiftVariantIds.has(variantId)) {
          const success = await this.#removeGift(item.key);
          if (success) cartChanged = true;
        }
      }

      // Add gifts that are qualified but not yet in the cart
      for (const variantId of desiredGiftVariantIds) {
        if (!existingGifts.has(variantId)) {
          const message = desiredGiftMessages.get(variantId) || '';
          const success = await this.#addGift(variantId, message);
          if (success) cartChanged = true;
        }
      }

      // Refresh the cart UI if any changes were made
      if (cartChanged) {
        this.#refreshCartUI();
      }
    } catch (error) {
      console.error('[cart-gift-manager] Error evaluating gifts:', error);
    } finally {
      this.#isProcessing = false;
    }
  }
}

export const cartGiftManager = new CartGiftManager();

cartGiftManager.init();
